import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../shared/hook";
import Pagination from "../../shared/components/Table/Pagination";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import { deleteBalloon, getBalloonsList } from "../../common/api/baloonsApi";
import { t } from "i18next";
import { Tooltip } from "react-tooltip";
import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import style from "./CilinderList.module.css";
import SelectCustomFormik from "../../shared/Ui/SelectCustom/SelectCustomFormik";
import CalendarSelectFormik from "../../shared/Ui/Calendar/CalendarSelectFormik";
import { log } from "node:console";
import ActionDropwdownCell from "../../shared/components/Table/ActionDropwdownCell";
import ModalButton from "../../shared/Ui/Buttons/ModalButtons";
import ModalComponent from "../../shared/Ui/ModalComponent/ModalWindow";

type PaginationState = {
  pageIndex: number;
  pageSize: number;
};

function GasCilindersList() {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pageSize = useAppSelector((state) => state.userSettings.pageSize);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  });
  const [tableData, setTableData] = useState<{
    currentPage?: number;
    itemsPerPage?: number;
    totalItems: number;
    totalPages: number;
  }>();
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [values, setValues] = useState<any[]>([]);
  const handleTooltipToggle = (id: number) => {
    setActiveTooltipId((prevId) => (prevId === id ? null : id));
  };
  const [activeTooltipId, setActiveTooltipId] = useState<number | null>(null);
  const [acceptModal, setAcceptModal] = useState<any>(null);
  const [initialValues, setInitialValues] = useState({
    factoryNumber: "",
    balloonCode: "",
    emptyWeightFrom: "",
    emptyWeightTo: "",
    gasWeightFrom: "",
    gasWeightTo: "",
    material: "",
    country: "",
    productionDateFrom: "",
    productionDateTo: "",
    workingPressureFrom: "",
    workingPressureTo: "",
    testPressureFrom: "",
    testPressureTo: "",
    lastInspectionDateFrom: "",
    lastInspectionDateTo: "",
    lastRepairDateFrom: "",
    lastRepairDateTo: "",
    repairResponsible: "",
    nextInspectionDateFrom: "",
    nextInspectionDateTo: ""
  });

  const loadList = () => {
    getBalloonsList(
      pagination.pageIndex + 1,
      pagination.pageSize,
      "DESC",
      initialValues as any
    )
      .then((data: any) => {
        const arr =
          data?.gasBalloons ?? (Array.isArray(data) ? data : (data?.values ?? []));
        setValues(arr);
        setTableData({
          currentPage: data?.currentPage,
          itemsPerPage: data?.itemsPerPage,
          totalItems: data?.totalItems ?? (Array.isArray(arr) ? arr.length : 0),
          totalPages: data?.totalPages
        });
      })
  };

  useEffect(() => {
    loadList();
  }, [pagination]);

  const calculateItemNumber = (index: number) => {
    return pagination.pageIndex * pagination.pageSize + index + 1;
  };

  const renderFilterInputs = () => {
    return (
      <Formik
        initialValues={initialValues}
        onSubmit={() => {
          setPagination((prevValues: PaginationState) => {
            return { ...prevValues, pageIndex: 0 };
          })
          setIsFilterOpen(false);
        }}
        validateOnBlur={true}
        validateOnChange={true}
      >
        {(formik) => (
          <Form className="max-w-[1585px] w-full pb-[15px]">
            <div className="flex flex-row align-center justify-between">
              <p className="my-3 text-lg font-semibold">
                {t("documents_upload.filters")}
              </p>
              <button
                type="reset"
                onClick={() => {
                  setIsFilterOpen(false);
                }}
              >
                <img src="/icons/close.svg" />
              </button>
            </div>
            <div className={style.inputField}>
              <div className={style.labelAndButtonWrapper}>
                <label className={style.inputLabel}>
                  {t("documents_upload.balloonCode")}
                </label>
              </div>
              <Field
                type="text"
                name="balloonCode"
                placeholder={t("documentLabels.choose_option")}
                className={style.placeholder}
                value={formik?.values?.balloonCode}
                onChange={(e: any) => {
                  formik.setFieldValue("balloonCode", e.target.value);
                  setInitialValues((prevValues: any) => {
                    return { ...prevValues, balloonCode: e.target.value };
                  });
                }}
              />
            </div>
            <div className="flex flex-row gap-[20px]">
              <div className={style.inputField}>
                <div className={style.labelAndButtonWrapper}>
                  <label className={style.inputLabel}>
                    {t("documents_upload.factoryNumber")}
                  </label>
                </div>
                <Field
                  type="text"
                  name="factoryNumber"
                  placeholder={t("documentLabels.choose_option")}
                  className={style.placeholder}
                  value={formik?.values?.factoryNumber}
                  onChange={(e: any) => {
                    formik.setFieldValue("factoryNumber", e.target.value);
                    setInitialValues((prevValues: any) => {
                      return { ...prevValues, factoryNumber: e.target.value };
                    });
                  }}
                />
              </div>
              <div className={style.inputField}>
                <div className={style.labelAndButtonWrapper}>
                  <label className={style.inputLabel}>
                    {t("documents_upload.repairResponsible")}
                  </label>
                </div>
                <Field
                  type="text"
                  name="repairResponsible"
                  placeholder={t("documentLabels.choose_option")}
                  className={style.placeholder}
                  value={formik?.values?.repairResponsible}
                  onChange={(e: any) => {
                    formik.setFieldValue("repairResponsible", e.target.value);
                    setInitialValues((prevValues: any) => {
                      return { ...prevValues, repairResponsible: e.target.value };
                    });
                  }}
                />
              </div>
            </div>

            <div className="flex flex-row gap-[20px]">

              <div className="flex flex-col">
                {/* <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.emptyWeight")}
                    </label>
                  </div>
                  <Field
                    type="text"
                    name="emptyWeight"
                    placeholder={t("documentLabels.choose_option")}
                    className={style.placeholder}
                    value={formik?.values?.emptyWeight}
                    onChange={(e: any) => {
                      formik.setFieldValue("emptyWeight", e.target.value);
                      setInitialValues((prevValues: any) => {
                        return { ...prevValues, emptyWeight: e.target.value };
                      });
                    }}
                  />
                </div> */}

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.emptyWeight")}
                    </label>
                  </div>
                  <div className={style.twoInputsRow}>
                    <div className={style.smallInput}>
                      <Field
                        type="text"
                        name="emptyWeightFrom"
                        placeholder={t("documentLabels.from")}
                        className={style.placeholder}
                        value={formik?.values?.emptyWeightFrom}
                        onChange={(e: any) => {
                          formik.setFieldValue("emptyWeightFrom", e.target.value);
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, emptyWeightFrom: e.target.value };
                          });
                        }}
                      />
                    </div>
                    <div className={style.smallInput}>
                      <Field
                        type="text"
                        name="emptyWeightTo"
                        placeholder={t("documentLabels.to")}
                        className={style.placeholder}
                        value={formik?.values?.emptyWeightTo}
                        onChange={(e: any) => {
                          formik.setFieldValue("emptyWeightTo", e.target.value);
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, emptyWeightTo: e.target.value };
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.gasWeight")}
                    </label>
                  </div>
                  <Field
                    type="text"
                    name="gasWeight"
                    placeholder={t("documentLabels.choose_option")}
                    className={style.placeholder}
                    value={formik?.values?.gasWeight}
                    onChange={(e: any) => {
                      formik.setFieldValue("gasWeight", e.target.value);
                      setInitialValues((prevValues: any) => {
                        return { ...prevValues, gasWeight: e.target.value };
                      });
                    }}
                  />
                </div> */}

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.gasWeight")}
                    </label>
                  </div>
                  <div className={style.twoInputsRow}>
                    <div className={style.smallInput}>
                      <Field
                        type="text"
                        name="gasWeightFrom"
                        placeholder={t("documentLabels.from")}
                        className={style.placeholder}
                        value={formik?.values?.gasWeightFrom}
                        onChange={(e: any) => {
                          formik.setFieldValue("gasWeightFrom", e.target.value);
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, gasWeightFrom: e.target.value };
                          });
                        }}
                      />
                    </div>
                    <div className={style.smallInput}>
                      <Field
                        type="text"
                        name="gasWeightTo"
                        placeholder={t("documentLabels.to")}
                        className={style.placeholder}
                        value={formik?.values?.gasWeightTo}
                        onChange={(e: any) => {
                          formik.setFieldValue("gasWeightTo", e.target.value);
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, gasWeightTo: e.target.value };
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.workingPressure")}
                    </label>
                  </div>
                  <Field
                    type="text"
                    name="workingPressure"
                    placeholder={t("documentLabels.choose_option")}
                    className={style.placeholder}
                    value={formik?.values?.workingPressure}
                    onChange={(e: any) => {
                      formik.setFieldValue("workingPressure", e.target.value);
                      setInitialValues((prevValues: any) => {
                        return { ...prevValues, workingPressure: e.target.value };
                      });
                    }}
                  />
                </div> */}

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.workingPressure")}
                    </label>
                  </div>
                  <div className={style.twoInputsRow}>
                    <div className={style.smallInput}>
                      <Field
                        type="text"
                        name="workingPressureFrom"
                        placeholder={t("documentLabels.from")}
                        className={style.placeholder}
                        value={formik?.values?.workingPressureFrom}
                        onChange={(e: any) => {
                          formik.setFieldValue("workingPressureFrom", e.target.value);
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, workingPressureFrom: e.target.value };
                          });
                        }}
                      />
                    </div>
                    <div className={style.smallInput}>
                      <Field
                        type="text"
                        name="workingPressureTo"
                        placeholder={t("documentLabels.to")}
                        className={style.placeholder}
                        value={formik?.values?.workingPressureTo}
                        onChange={(e: any) => {
                          formik.setFieldValue("workingPressureTo", e.target.value);
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, workingPressureTo: e.target.value };
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.testPressure")}
                    </label>
                  </div>
                  <Field
                    type="text"
                    name="testPressure"
                    placeholder={t("documentLabels.choose_option")}
                    className={style.placeholder}
                    value={formik?.values?.testPressure}
                    onChange={(e: any) => {
                      formik.setFieldValue("testPressure", e.target.value);
                      setInitialValues((prevValues: any) => {
                        return { ...prevValues, testPressure: e.target.value };
                      });
                    }}
                  />
                </div> */}

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.testPressure")}
                    </label>
                  </div>
                  <div className={style.twoInputsRow}>
                    <div className={style.smallInput}>
                      <Field
                        type="text"
                        name="testPressureFrom"
                        placeholder={t("documentLabels.from")}
                        className={style.placeholder}
                        value={formik?.values?.testPressureFrom}
                        onChange={(e: any) => {
                          formik.setFieldValue("testPressureFrom", e.target.value);
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, testPressureFrom: e.target.value };
                          });
                        }}
                      />
                    </div>
                    <div className={style.smallInput}>
                      <Field
                        type="text"
                        name="testPressureTo"
                        placeholder={t("documentLabels.to")}
                        className={style.placeholder}
                        value={formik?.values?.testPressureTo}
                        onChange={(e: any) => {
                          formik.setFieldValue("testPressureTo", e.target.value);
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, testPressureTo: e.target.value };
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documentLabels.material")}
                    </label>
                  </div>
                  <Field
                    value={formik?.values?.material || null}
                    component={SelectCustomFormik}
                    name="material"
                    placeholder={t("documentLabels.choose")}
                    isClearable={true}
                    isMulti={false}
                    size="dm"
                    options={[
                      { value: "COMPOSITE", label: t("GasCilinder.materialComposite") },
                      {
                        value: "STEEL",
                        label: t("GasCilinder.materialSteel"),
                      },
                    ]}
                    onChange={(options: any) => {
                      formik.setFieldValue("material", options);
                      setInitialValues((prevValues: any) => {
                        return { ...prevValues, material: options };
                      });
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col">

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.productionDate")}
                    </label>
                  </div>

                  <div className={style.twoInputsRow}>
                    <div className={style.smallInput}>
                      <CalendarSelectFormik
                        name="productionDateFrom"
                        documentStatus="new"
                        outputFormat="yyyy-MM"
                        showMonthYearSelect={true}
                        placeholder={t("documentLabels.from")}
                        onChange={(val?: string) => {
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, productionDateFrom: val || null };
                          });
                        }}
                      />
                    </div>

                    <div className={style.smallInput}>
                      <CalendarSelectFormik
                        name="productionDateTo"
                        documentStatus="new"
                        outputFormat="yyyy-MM"
                        showMonthYearSelect={true}
                        placeholder={t("documentLabels.to")}
                        onChange={(val?: string) => {
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, productionDateTo: val || null };
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.lastInspectionDate")}
                    </label>
                  </div>
                  <Field
                    type="text"
                    name="lastInspectionDate"
                    placeholder={t("documentLabels.choose_option")}
                    className={style.placeholder}
                    value={formik?.values?.lastInspectionDate}
                    onChange={(e: any) => {
                      formik.setFieldValue("lastInspectionDate", e.target.value);
                      setInitialValues((prevValues: any) => {
                        return { ...prevValues, lastInspectionDate: e.target.value };
                      });
                    }}
                  />
                </div> */}

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.lastInspectionDate")}
                    </label>
                  </div>

                  <div className={style.twoInputsRow}>
                    <div className={style.smallInput}>
                      <CalendarSelectFormik
                        name="lastInspectionDateFrom"
                        documentStatus="new"
                        outputFormat="yyyy-MM"
                        showMonthYearSelect={true}
                        placeholder={t("documentLabels.from")}
                        onChange={(val?: string) => {
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, lastInspectionDateFrom: val || null };
                          });
                        }}
                      />
                    </div>

                    <div className={style.smallInput}>
                      <CalendarSelectFormik
                        name="lastInspectionDateTo"
                        documentStatus="new"
                        outputFormat="yyyy-MM"
                        showMonthYearSelect={true}
                        placeholder={t("documentLabels.to")}
                        onChange={(val?: string) => {
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, lastInspectionDateTo: val || null };
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.nextInspectionDate")}
                    </label>
                  </div>
                  <Field
                    type="text"
                    name="nextInspectionDate"
                    placeholder={t("documentLabels.choose_option")}
                    className={style.placeholder}
                    value={formik?.values?.nextInspectionDate}
                    onChange={(e: any) => {
                      formik.setFieldValue("nextInspectionDate", e.target.value);
                      setInitialValues((prevValues: any) => {
                        return { ...prevValues, nextInspectionDate: e.target.value };
                      });
                    }}
                  />
                </div> */}

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.nextInspectionDateFrom")}
                    </label>
                  </div>

                  <div className={style.twoInputsRow}>
                    <div className={style.smallInput}>
                      <CalendarSelectFormik
                        name="nextInspectionDateFrom"
                        documentStatus="new"
                        outputFormat="yyyy-MM"
                        showMonthYearSelect={true}
                        placeholder={t("documentLabels.from")}
                        onChange={(val?: string) => {
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, nextInspectionDateFrom: val || null };
                          });
                        }}
                      />
                    </div>

                    <div className={style.smallInput}>
                      <CalendarSelectFormik
                        name="nextInspectionDateTo"
                        documentStatus="new"
                        outputFormat="yyyy-MM"
                        showMonthYearSelect={true}
                        placeholder={t("documentLabels.to")}
                        onChange={(val?: string) => {
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, nextInspectionDateTo: val || null };
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.lastRepairDate")}
                    </label>
                  </div>
                  <Field
                    type="text"
                    name="lastRepairDate"
                    placeholder={t("documentLabels.choose_option")}
                    className={style.placeholder}
                    value={formik?.values?.lastRepairDate}
                    onChange={(e: any) => {
                      formik.setFieldValue("lastRepairDate", e.target.value);
                      setInitialValues((prevValues: any) => {
                        return { ...prevValues, lastRepairDate: e.target.value };
                      });
                    }}
                  />
                </div> */}

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documents_upload.lastRepairDate")}
                    </label>
                  </div>

                  <div className={style.twoInputsRow}>
                    <div className={style.smallInput}>
                      <CalendarSelectFormik
                        name="lastRepairDateFrom"
                        documentStatus="new"
                        outputFormat="dd.MM.yyyy"
                        placeholder={t("documentLabels.from")}
                        onChange={(val?: string) => {
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, lastRepairDateFrom: val || null };
                          });
                        }}
                      />
                    </div>

                    <div className={style.smallInput}>
                      <CalendarSelectFormik
                        name="lastRepairDateTo"
                        documentStatus="new"
                        outputFormat="dd.MM.yyyy"
                        placeholder={t("documentLabels.to")}
                        onChange={(val?: string) => {
                          setInitialValues((prevValues: any) => {
                            return { ...prevValues, lastRepairDateTo: val || null };
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("documentLabels.country")}
                    </label>
                  </div>
                  <Field
                    value={formik?.values?.country || null}
                    component={SelectCustomFormik}
                    name="country"
                    placeholder={t("documentLabels.choose")}
                    isClearable={true}
                    isMulti={false}
                    size="dm"
                    options={[
                      { value: "KAZAKHSTAN", label: t("GasCilinder.countryKazakhstan") },
                      {
                        value: "OTHER",
                        label: t("GasCilinder.countryOther"),
                      },
                    ]}
                    onChange={(options: any) => {
                      formik.setFieldValue("country", options);
                      setInitialValues((prevValues: any) => {
                        return { ...prevValues, country: options };
                      });
                    }}
                  />
                </div>
              </div>

            </div>

            {/* <Calendar
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
            /> */}

            <div className="flex flex-row gap-[10px] justify-end mt-2">
              <button
                type="submit"
                className=" bg-green text-white font-semibold py-2 px-4 rounded"
              >
                {t("mainButtons3.apply")}
              </button>
              <button
                type="reset"
                className=" bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
                onClick={() => {
                  console.log(initialValues);
                  console.log(formik.values);
                  setStartDate(undefined);
                  setEndDate(undefined);
                  setInitialValues({
                    factoryNumber: "",
                    balloonCode: "",
                    emptyWeightFrom: "",
                    emptyWeightTo: "",
                    gasWeightFrom: "",
                    gasWeightTo: "",
                    material: "",
                    country: "",
                    productionDateFrom: "",
                    productionDateTo: "",
                    workingPressureFrom: "",
                    workingPressureTo: "",
                    testPressureFrom: "",
                    testPressureTo: "",
                    lastInspectionDateFrom: "",
                    lastInspectionDateTo: "",
                    lastRepairDateFrom: "",
                    lastRepairDateTo: "",
                    repairResponsible: "",
                    nextInspectionDateFrom: "",
                    nextInspectionDateTo: ""
                  });
                  formik.resetForm({
                    values: {
                      factoryNumber: "",
                      balloonCode: "",
                      emptyWeightFrom: "",
                      emptyWeightTo: "",
                      gasWeightFrom: "",
                      gasWeightTo: "",
                      material: "",
                      country: "",
                      productionDateFrom: "",
                      productionDateTo: "",
                      workingPressureFrom: "",
                      workingPressureTo: "",
                      testPressureFrom: "",
                      testPressureTo: "",
                      lastInspectionDateFrom: "",
                      lastInspectionDateTo: "",
                      lastRepairDateFrom: "",
                      lastRepairDateTo: "",
                      repairResponsible: "",
                      nextInspectionDateFrom: "",
                      nextInspectionDateTo: ""
                    },
                  });
                  setPagination((prevValues: PaginationState) => {
                    return { ...prevValues, pageIndex: 0 };
                  });
                }}
              >
                {t("mainButtons3.reset")}
              </button>
            </div>
          </Form>
        )
        }
      </Formik >
    );
  };

  return (
    <>
      <ModalComponent
        isOpen={!!errorMessage}
        onClose={() => {
          setErrorMessage(null);
        }}
        headerContent={
          <h1 className={style.modal__header}>
            {t("documentNotification.notification")}
          </h1>
        }
        content={
          <p className={style.modal__text}>
            {errorMessage}
          </p>
        }
         customContent={
          <div className="flex flex-row gap-[10px]">
            <ModalButton
              type="button"
              color="green"
              onClick={() => {
                setErrorMessage(null);
              }}
            >
              {t("orderList.accept")}
            </ModalButton>
          </div>
        }
      />
      <ModalComponent
        isOpen={acceptModal}
        onClose={() => {
          setAcceptModal(null);
        }}
        headerContent={
          <h1 className="h__modal">{t("orderList.notification")}</h1>
        }
        content={
          <p className="p__modal">
            {t("orderList.orderDelete")} №{acceptModal?.number}?
          </p>
        }
        customContent={
          <div className="flex flex-row gap-[10px]">
            <ModalButton
              type="button"
              onClick={() => {
                setAcceptModal(null);
              }}
            >
              {t("orderList.cancel")}
            </ModalButton>
            <ModalButton
              color="green"
              type="button"
              onClick={async () => {
                try {
                  const response = await deleteBalloon(acceptModal.id)
                  if (response?.status === "ERROR" && response?.message === "delete.has-relations") {
                    setErrorMessage(t("GasCilinder.delete_has_relations"))
                  } else {
                    setAcceptModal(null)
                    loadList()
                  }
                } catch (error: any) {
                  const apiStatus = error?.response?.data?.status
                  const apiMessage = error?.response?.data?.message
                  if (apiStatus === "ERROR" && apiMessage === "delete.has-relations") {
                    setErrorMessage(t("GasCilinder.delete_has_relations"))
                  }
                } finally {
                  setAcceptModal(false)
                }
              }}
            >
              {t("orderList.delete")}
            </ModalButton>
          </div>
        }
      />
      <div className="flex flex-1 flex-col min-w-0">
        <h2 className="my-3 text-xl font-semibold">Список газовых баллонов</h2>
        <div className="bg-white my-5 p-5 pt-8 relative rounded-xl shadow-md flex flex-row justify-between">
          <button
            type="button"
            className="bg-green text-white font-semibold py-2 px-4 rounded"
            onClick={() => navigate("/gasCilinders/new")}
          >
            Создать
          </button>
          <div className="flex gap-[10px]">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsFilterOpen(true);
              }}
              className="bg-green text-white font-semibold py-2 px-4 rounded"
              data-tooltip-offset={4}
              data-tooltip-id="filter-type"
            >
              {t("mainButtons3.filter")}
            </button>

            <button
              type="button"
              onClick={() => {
                loadList();
              }}
            >
              <img src="/icons/update.svg" alt="update" />
            </button>
          </div>

        </div>

        <div className="w-full">
          <div className="rounded-lg overflow-x-auto bg-white">
            <table className="max-w-full w-full leading-normal">
              <thead className="border-b-2 border-gray-300">
                <tr>
                  <th
                    className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                    scope="col"
                  >
                    №
                  </th>
                  <th
                    className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                    scope="col"
                  >
                    Заводской номер бытового баллона
                  </th>
                  <th
                    className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                    scope="col"
                  >
                    Уникальный код баллона
                  </th>
                  <th
                    className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                    scope="col"
                  >
                    Материал баллона
                  </th>
                  <th
                    className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                    scope="col"
                  >
                    Страна производства
                  </th>
                  <th
                    className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                    scope="col"
                  >
                    Дата производства
                  </th>
                  <th
                    className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                    scope="col"
                  >
                    Дата последней поверки (ТО)
                  </th>
                </tr>
              </thead>
              {values.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <div>
                        <EmptyListPlaceholderContent title="Данные отсутствуют" />
                      </div>
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody className="font-normal text-center">
                  {values
                    .map((value: any, index: number) => (
                      <tr
                        key={`tr-${index}-${value?.id || index}`}
                        className={`${value?.id ? "cursor-pointer hover:bg-gray-100" : ""}`}
                        onClick={() => {
                          if (value?.id) navigate(`/gasCilinders/${value.id}`);
                        }}
                      >
                        <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                          {calculateItemNumber(index)}
                        </td>
                        <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                          {value?.factoryNumber ?? "-"}
                        </td>
                        <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                          {value?.balloonCode ?? "-"}
                        </td>
                        <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                          {value?.material == "STEEL" ? t("GasCilinder.materialSteel") : t("GasCilinder.materialComposite")}
                        </td>
                        <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                          {value?.country === "KAZAKHSTAN" ? t("GasCilinder.countryKazakhstan") : t("GasCilinder.countryOther")}
                        </td>
                        <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                          {value?.productionDate ?? "-"}
                        </td>
                        <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                          {value?.lastInspectionDate ?? "-"}
                        </td>
                        <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ActionDropwdownCell
                            key={`cell-${index}-${value.id}-acitons`}
                            id={value.id}
                            actions={[
                              {
                                title: t("orderList.show"),
                                action: () => {
                                  navigate("/gasCilinders/" + value?.id);
                                },
                              },
                              ...(value?.canDelete ? [{
                                title: t("orderList.delete"),
                                action: (id: any) => {
                                  setAcceptModal({
                                    id: value.id,
                                    number: value.balloonCode,
                                  });
                                },
                              }] : []),
                            ]}
                            activeTooltipId={activeTooltipId}
                            onTooltipToggle={handleTooltipToggle}
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              )}
            </table>
          </div>
          <Pagination
            totalItems={tableData?.totalItems || 0}
            totalPages={tableData?.totalPages || 0}
            pagination={pagination}
            setPagination={setPagination}
          />
        </div>
        <div className="z-[1000] relative	">
          <Tooltip
            isOpen={isFilterOpen}
            id="filter-type"
            className={classNames(style.wrapperTooltip, "drop-shadow-2xl")}
            openOnClick={true}
            clickable={true}
            noArrow={true}
            opacity={1}
            place={"bottom-end"}
            afterShow={() => setIsFilterOpen(true)}
            afterHide={() => setIsFilterOpen(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>{renderFilterInputs()}</div>
          </Tooltip>
        </div>
      </div>
    </>
  );
}

export default GasCilindersList;


